import { useEffect, useRef } from "react";
import { FocusGuard } from "@/components/game/FocusGuard";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { PauseCircle, WifiOff, Flame, Trophy } from "lucide-react";
import { useRemaining, useRoomSocket } from "@/lib/ws";
import { getMode } from "@/modes";
import { PeriodicTableButton } from "@/components/game/PeriodicTableButton";
import { StudentLobby } from "@/components/game/StudentLobby";
import { NicknameForm } from "@/components/game/NicknameForm";
import { teamById } from "@/components/game/InitialDot";
import { Mascot } from "@/components/game/Mascot";
import { Button } from "@/components/ui/button";
import { studentTokenKey } from "@/pages/JoinPage";
import { sfx } from "@/lib/sfx";

function useStreakSound(state) {
  const last = useRef(null);
  useEffect(() => {
    const onTap = () => sfx.enable();
    window.addEventListener("pointerdown", onTap, { once: true });
    return () => window.removeEventListener("pointerdown", onTap);
  }, []);
  useEffect(() => {
    const mine = state?.results?.mine;
    if (state?.phase !== "results" || mine?.status !== "correct" || mine.streak < 3 || last.current === state.q_index) return;
    last.current = state.q_index;
    if (state.settings?.sound !== false) sfx.streak();
  }, [state]);
}

const Header = ({ state, connected }) => {
  const team = teamById(state.teams, state.me.team_id);
  return (
    <header className="flex items-center gap-3 pb-4 border-b border-[rgba(71,85,105,0.45)]">
      {state.me.mascot && <Mascot id={state.me.mascot} size={44} className="anim-bob shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="font-display text-xl font-bold truncate" data-testid="student-name">{state.me.first_name} {state.me.last_name}</p>
        {team && <p className="text-sm font-semibold" style={{ color: team.color }} data-testid="student-team">{team.name}</p>}
        {state.me.nickname && <p className="text-sm text-slate-400" data-testid="student-nickname">Takma ad: <b className="text-[#FDE047]">{state.me.nickname}</b></p>}
      </div>
      {state.fire && <span className="flex items-center gap-1 text-lg font-bold text-amber-300" data-testid="student-fire"><Flame className="h-6 w-6 text-[#FACC15] anim-flame" />Ateş serisi ({state.streak})</span>}
      {!connected && <WifiOff className="h-5 w-5 text-red-400" data-testid="student-offline" />}
    </header>
  );
};

const ClassStrip = ({ state }) => {
  if (state.class_total === undefined) return null;
  const best = state.class_best;
  const beaten = best !== null && best !== undefined && state.class_total > best;
  return (
    <div className="mt-3 flex items-center gap-2 rounded-full border border-[#FACC15]/50 bg-[#FACC15]/10 px-4 py-2 text-sm" data-testid="student-class-strip">
      <Trophy className="h-4 w-4 text-[#FACC15]" />
      <span>Sınıf puanı <b className="text-[#FACC15]" data-testid="student-class-total">{state.class_total}</b></span>
      <span className={`ml-auto font-semibold ${beaten ? "text-[#4ADE80]" : "text-slate-300"}`} data-testid="student-class-best">
        {beaten ? "Yeni sınıf rekoru!" : best === null || best === undefined ? "İlk rekoru siz koyun!" : `Sınıf rekoru ${best}`}
      </span>
    </div>
  );
};

const Kicked = () => (
  <div className="min-h-screen p-8 space-y-4" data-testid="student-kicked">
    <h1 className="font-display text-3xl font-black">Oyundan çıkarıldın</h1>
    <p className="text-slate-300">Öğretmenin seni bu oyundan çıkardı.</p>
    <Button onClick={() => { window.location.href = "/"; }} data-testid="kicked-home-button">Ana sayfa</Button>
  </div>
);

export default function StudentGame() {
  const { code } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(studentTokenKey(code));
  const { state, connected, fatal, send, deadline } = useRoomSocket(token ? `/student/${code}?token=${encodeURIComponent(token)}` : null);
  const remaining = useRemaining(state, deadline);
  useStreakSound(state);

  useEffect(() => {
    if (fatal) localStorage.removeItem(studentTokenKey(code));
    if (fatal === "not_found") navigate(`/katil/${code}`, { replace: true });
  }, [fatal, code, navigate]);

  if (!token) return <Navigate to={`/katil/${code}`} replace />;
  if (fatal === "kicked") return <Kicked />;
  if (!state) return <p className="p-8 eyebrow" data-testid="student-connecting">Bağlanılıyor…</p>;
  const Mode = getMode(state.mode);

  return (
    <div className={`min-h-screen px-4 py-4 max-w-lg mx-auto pb-24 ${state.settings?.animations === false ? "no-anim" : ""}`} data-testid="student-game">
      <Header state={state} connected={connected} />
      <ClassStrip state={state} />
      <main className="pt-5">
        {state.needs_nickname ? <NicknameForm send={send} /> : state.paused ? (
          <div className="flex flex-col items-center gap-4 py-16 text-center" data-testid="student-paused">
            <PauseCircle className="h-16 w-16 text-[#FACC15]" />
            <p className="font-display text-3xl font-black">Oyun duraklatıldı</p>
            <p className="text-slate-400">Öğretmen devam ettirince kaldığın yerden sürer.</p>
          </div>
        ) : state.phase === "lobby" ? <StudentLobby state={state} send={send} /> : <Mode.StudentView state={state} send={send} remaining={remaining} />}
      </main>
      <PeriodicTableButton />
      <FocusGuard send={send} connected={connected} />
    </div>
  );
}
